import React, { useState, useRef, useEffect } from 'react';
import './PinAuth.css';
import apiService from '../services/api';
import { Server } from '../services/api';

interface PinAuthProps {
  onPinSuccess: (server: Server) => void;
}

const PinAuth: React.FC<PinAuthProps> = ({ onPinSuccess }) => {
  const [pin, setPin] = useState(['', '', '', '']);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [servers, setServers] = useState<Server[]>([]);
  const [isServersLoaded, setIsServersLoaded] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Fetch server information when component mounts
    const fetchServers = async () => {
      try {
        setIsLoading(true);
        const serverData = await apiService.getCaissier();
        
        // Ensure serverData is an array
        if (Array.isArray(serverData)) {
          setServers(serverData);
        } else {
          console.error('Server data is not an array:', serverData);
          setError('Erreur: Format de données serveur invalide');
        }
        setIsServersLoaded(true);
      } catch (error) {
        console.error('Error fetching server data:', error);
        setError('Erreur de connexion au serveur');
        setIsServersLoaded(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchServers();
  }, []);

  useEffect(() => {
    if (inputRefs.current[currentIndex]) {
      inputRefs.current[currentIndex]?.focus();
    }
  }, [currentIndex]);

  const handlePinChange = (index: number, value: string) => {
    if (value.length > 1) return; // Only allow single character
    
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError('');

    // Move to next input if value is entered
    if (value && index < 3) {
      setCurrentIndex(index + 1);
    }

    // Check if PIN is complete
    if (newPin.every(digit => digit !== '')) {
      const pinString = newPin.join('');
      
      // Only validate if servers are loaded and is an array
      if (isServersLoaded && Array.isArray(servers) && servers.length > 0) {
        const matchedServer = apiService.validatePin(pinString, servers);
        
        if (matchedServer) {
          handlePinSuccess(matchedServer);
        } else {
          setError('PIN incorrect. Veuillez réessayer.');
          setPin(['', '', '', '']);
          setCurrentIndex(0);
          setTimeout(() => {
            inputRefs.current[0]?.focus();
          }, 100);
        }
      } else if (!isServersLoaded) {
        setError('Chargement des données serveur en cours...');
        setPin(['', '', '', '']);
        setCurrentIndex(0);
      } else {
        setError('Aucune donnée serveur disponible');
        setPin(['', '', '', '']);
        setCurrentIndex(0);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      setCurrentIndex(index - 1);
    }
  };

  const handlePinSuccess = (server: Server) => {
    setIsLoading(true);
    // Simulate API call delay
    setTimeout(() => {
      setIsLoading(false);
      onPinSuccess(server);
    }, 1000);
  };

  const handleInputClick = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <div className="auth-container">
      <div className="pin-auth-card card fade-in">
        <div className="card-header">
          <div className="auth-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <circle cx="12" cy="16" r="1"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
        </div>
        
        <div className="card-body">
          <form onSubmit={(e) => e.preventDefault()} className="pin-form">
            <div className="pin-inputs">
              {pin.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handlePinChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onClick={() => handleInputClick(index)}
                  className={`pin-input ${currentIndex === index ? 'active' : ''} ${digit ? 'filled' : ''}`}
                  disabled={isLoading || !isServersLoaded}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
              ))}
            </div>
            
            {error && (
              <div className="error-message">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                {error}
              </div>
            )}
            
            {isLoading && (
              <div className="loading-overlay">
                <div className="spinner"></div>
                <p>Vérification en cours...</p>
              </div>
            )}

            {!isServersLoaded && !isLoading && (
              <div className="loading-overlay">
                <div className="spinner"></div>
                <p>Chargement des données serveur...</p>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default PinAuth;
